import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { X, TrendingUp, AlertTriangle, Play, Check, Trash2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { COUNTRIES, countryName } from "@/lib/countries";
import { toast } from "sonner";

const DM_OPTIONS = ["CEO", "Founder", "Co-Founder", "Titolare", "Amministratore Unico", "Socio", "Presidente", "Direttore Generale", "CTO", "COO", "CFO", "Sales Director", "HR Director", "Marketing Director"];
const REGION_OPTIONS = ["Abruzzo", "Basilicata", "Calabria", "Campania", "Emilia-Romagna", "Friuli-Venezia Giulia", "Lazio", "Liguria", "Lombardia", "Marche", "Molise", "Piemonte", "Puglia", "Sardegna", "Sicilia", "Toscana", "Trentino-Alto Adige", "Umbria", "Valle d'Aosta", "Veneto"];
const HISTORY_OPTIONS = [7, 15, 30, 60, 90, 180];

function TagInput({ label, value, onChange, placeholder, testid }) {
  const [input, setInput] = useState("");
  const add = () => {
    const v = input.trim();
    if (v && !value.includes(v)) onChange([...value, v]);
    setInput("");
  };
  return (
    <div>
      <label className="text-sm font-semibold text-ink block mb-1.5">{label}</label>
      <div className="flex flex-wrap gap-1.5 mb-1.5">
        {value.map((t) => (
          <span key={t} className="inline-flex items-center gap-1 bg-brand-soft text-brand-dark rounded-full px-2.5 py-1 text-xs font-medium">
            {t}
            <button onClick={() => onChange(value.filter((x) => x !== t))}><X className="w-3 h-3" strokeWidth={2} /></button>
          </span>
        ))}
      </div>
      <input
        data-testid={testid}
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
        onBlur={add}
        placeholder={placeholder}
        className="w-full bg-white border border-line rounded-[6px] px-3 py-2 text-sm placeholder-faint focus:outline-none focus:ring-2 focus:ring-brand transition-colors"
      />
    </div>
  );
}

function ChipMulti({ label, options, value, onChange, testid }) {
  const toggle = (o) => onChange(value.includes(o) ? value.filter((x) => x !== o) : [...value, o]);
  return (
    <div>
      <label className="text-sm font-semibold text-ink block mb-1.5">{label}</label>
      <div className="flex flex-wrap gap-1.5" data-testid={testid}>
        {options.map((o) => (
          <button
            key={o}
            onClick={() => toggle(o)}
            className={`rounded-full px-3 py-1 text-xs font-medium border transition-colors ${
              value.includes(o) ? "bg-brand text-ink border-transparent" : "bg-white text-subtle border-line hover:bg-background"
            }`}
          >
            {o}
          </button>
        ))}
      </div>
    </div>
  );
}

function IndustryPicker({ industries, value, onChange, testid }) {
  const [q, setQ] = useState("");
  const macros = industries.filter((i) => i.parent_id === null);
  const filtered = q.trim()
    ? industries.filter((i) => `${i.it || ""} ${i.hierarchy || i.name}`.toLowerCase().includes(q.toLowerCase()))
    : macros;
  const toggle = (id) => onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  const selectedItems = industries.filter((i) => value.includes(i.id));
  const filteredIds = filtered.map((i) => i.id);
  const allVisibleSelected = filteredIds.length > 0 && filteredIds.every((id) => value.includes(id));
  const selectAllVisible = () => onChange(Array.from(new Set([...value, ...filteredIds])));
  const clearAll = () => onChange([]);
  return (
    <div>
      <label className="text-sm font-semibold text-ink block mb-1.5">Settore azienda (industry)</label>
      {selectedItems.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {selectedItems.map((i) => (
            <span key={i.id} className="inline-flex items-center gap-1 bg-brand-soft text-brand-dark rounded-full px-2.5 py-1 text-xs font-medium">
              {i.it || i.name}
              <button onClick={() => toggle(i.id)}><X className="w-3 h-3" strokeWidth={2} /></button>
            </span>
          ))}
        </div>
      )}
      <input
        data-testid={`${testid}-search`}
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Cerca settore (macro o sotto-categoria)…"
        className="w-full bg-white border border-line rounded-[6px] px-3 py-2 text-sm placeholder-faint focus:outline-none focus:ring-2 focus:ring-brand transition-colors mb-1.5"
      />
      <div className="flex items-center justify-between mb-1.5">
        <button
          type="button"
          data-testid="industry-select-all"
          onClick={selectAllVisible}
          disabled={allVisibleSelected}
          className="text-xs font-medium text-brand-dark hover:underline disabled:opacity-40 disabled:no-underline"
        >
          Seleziona tutto{q ? " (risultati)" : ""}
        </button>
        <button
          type="button"
          data-testid="industry-clear-all"
          onClick={clearAll}
          disabled={value.length === 0}
          className="text-xs font-medium text-subtle hover:text-destructive transition-colors disabled:opacity-40"
        >
          Deseleziona tutto{value.length ? ` (${value.length})` : ""}
        </button>
      </div>
      <div data-testid={testid} className="max-h-52 overflow-y-auto border border-line rounded-[6px] p-2 flex flex-col gap-0.5">
        {filtered.slice(0, 120).map((i) => (
          <label key={i.id} className="flex items-center gap-2 px-2 py-1.5 rounded-[6px] hover:bg-background cursor-pointer transition-colors">
            <input type="checkbox" checked={value.includes(i.id)} onChange={() => toggle(i.id)} className="accent-[#00E5A0] w-4 h-4" />
            <span className="text-sm text-ink flex-1">{i.it || (i.parent_id === null ? i.name : i.hierarchy)}</span>
            <span className="text-[10px] text-faint">{(i.companies || 0).toLocaleString("it-IT")}</span>
          </label>
        ))}
        {filtered.length === 0 && <p className="text-xs text-faint px-2 py-2">Nessun risultato.</p>}
      </div>
      <p className="text-[11px] text-faint mt-1">{q ? "Risultati filtrati (macro + sotto-categorie)" : "Categorie macro — cerca per vedere le sotto-categorie"}</p>
    </div>
  );
}

function EmployeesFilter({ min, max, onChange }) {
  const op = min != null && max != null ? "between" : min != null ? "gt" : max != null ? "lt" : "any";
  const setOp = (v) => {
    if (v === "any") onChange(null, null);
    else if (v === "gt") onChange(min ?? 10, null);
    else if (v === "lt") onChange(null, max ?? 500);
    else onChange(min ?? 10, max ?? 500);
  };
  return (
    <div>
      <label className="text-sm font-semibold text-ink block mb-1.5">Dipendenti azienda</label>
      <div className="flex gap-2 items-center">
        <select data-testid="employees-op" value={op} onChange={(e) => setOp(e.target.value)} className="bg-white border border-line rounded-[6px] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand transition-colors">
          <option value="any">Qualsiasi</option>
          <option value="gt">Maggiore di</option>
          <option value="lt">Minore di</option>
          <option value="between">Tra</option>
        </select>
        {(op === "gt" || op === "between") && (
          <input data-testid="employees-min" type="number" min={0} value={min ?? ""} onChange={(e) => onChange(e.target.value === "" ? null : parseInt(e.target.value), max)} placeholder="min" className="w-24 bg-white border border-line rounded-[6px] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand transition-colors" />
        )}
        {op === "between" && <span className="text-subtle text-sm">e</span>}
        {(op === "lt" || op === "between") && (
          <input data-testid="employees-max" type="number" min={0} value={max ?? ""} onChange={(e) => onChange(min, e.target.value === "" ? null : parseInt(e.target.value))} placeholder="max" className="w-24 bg-white border border-line rounded-[6px] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand transition-colors" />
        )}
      </div>
    </div>
  );
}

function CountryMultiSelect({ label, value, onChange, testid }) {
  const [q, setQ] = useState("");
  const filtered = q.trim()
    ? COUNTRIES.filter((c) => `${c.name} ${c.code}`.toLowerCase().includes(q.toLowerCase()))
    : COUNTRIES;
  const toggle = (code) => onChange(value.includes(code) ? value.filter((x) => x !== code) : [...value, code]);
  return (
    <div>
      <label className="text-sm font-semibold text-ink block mb-1.5">{label}</label>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {value.map((code) => (
            <span key={code} className="inline-flex items-center gap-1 bg-brand-soft text-brand-dark rounded-full px-2.5 py-1 text-xs font-medium">
              {countryName(code)}
              <button onClick={() => toggle(code)}><X className="w-3 h-3" strokeWidth={2} /></button>
            </span>
          ))}
        </div>
      )}
      <input
        data-testid={`${testid}-search`}
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Cerca paese…"
        className="w-full bg-white border border-line rounded-[6px] px-3 py-2 text-sm placeholder-faint focus:outline-none focus:ring-2 focus:ring-brand transition-colors mb-1.5"
      />
      <div data-testid={testid} className="max-h-44 overflow-y-auto border border-line rounded-[6px] p-2 flex flex-col gap-0.5">
        {filtered.map((c) => (
          <label key={c.code} className="flex items-center gap-2 px-2 py-1.5 rounded-[6px] hover:bg-background cursor-pointer transition-colors">
            <input type="checkbox" checked={value.includes(c.code)} onChange={() => toggle(c.code)} className="accent-[#00E5A0] w-4 h-4" />
            <span className="text-sm text-ink flex-1">{c.name}</span>
            <span className="text-[10px] text-faint">{c.code}</span>
          </label>
        ))}
        {filtered.length === 0 && <p className="text-xs text-faint px-2 py-2">Nessun paese.</p>}
      </div>
    </div>
  );
}

function DmTargetInput({ value, onChange, suggestions }) {
  const add = (v) => { if (v && !value.includes(v)) onChange([...value, v]); };
  const available = suggestions.filter((s) => !value.includes(s));
  return (
    <div>
      <TagInput testid="dm-input" label="Decision Maker target (ruoli/titoli da cercare)" value={value} onChange={onChange} placeholder="Digita un ruolo e premi Invio (es. Titolare, Sales Director)" />
      {available.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          <span className="text-[11px] text-faint self-center">Suggerimenti:</span>
          {available.map((s) => (
            <button
              key={s}
              type="button"
              data-testid={`dm-suggest-${s}`}
              onClick={() => add(s)}
              className="rounded-full px-2.5 py-1 text-xs font-medium border border-line bg-white text-subtle hover:bg-background transition-colors"
            >
              + {s}
            </button>
          ))}
        </div>
      )}
      <p className="text-[11px] text-faint mt-1.5">Testo libero: questi titoli verranno usati per identificare le persone in azienda in fase di enrichment.</p>
    </div>
  );
}

const EMPTY = {
  name: "", keywords: [], exclude_keywords: [], body_keywords: [], body_exclude_keywords: [], target_dms: [], target_dm_location: [], industries: [], industry_ids: [],
  company_sizes: [], min_employees: null, max_employees: null,
  company_country_codes: ["IT"], company_cities: [],
  company_zones: [], job_country_codes: ["IT"], job_regions: [], tech_stack: [],
  contract_type: null, company_type: null, remote: null, history_days: 30, import_limit: null, status: "bozza",
};

export default function PositionEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [industries, setIndustries] = useState([]);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pid, setPid] = useState(id || null);
  const [savedAt, setSavedAt] = useState(null);
  const lastSaved = useRef(null);
  const pidRef = useRef(id || null);
  const skipNextLoad = useRef(false);

  useEffect(() => {
    api.get("/catalog/industries").then((r) => setIndustries(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!id) return;
    if (skipNextLoad.current) { skipNextLoad.current = false; return; }
    api.get(`/positions/${id}`).then((r) => {
      const f = { ...EMPTY, ...r.data };
      lastSaved.current = JSON.stringify(f);
      pidRef.current = id;
      setForm(f);
    });
  }, [id]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const persist = useCallback(async (f) => {
    const body = { ...f };
    if (pidRef.current) {
      await api.put(`/positions/${pidRef.current}`, body);
    } else {
      const { data } = await api.post("/positions", body);
      pidRef.current = data.id;
      setPid(data.id);
      skipNextLoad.current = true;
      navigate(`/posizioni/${data.id}`, { replace: true });
    }
    lastSaved.current = JSON.stringify(f);
    setSavedAt(Date.now());
    return pidRef.current;
  }, [navigate]);

  // Salvataggio automatico (debounced) delle modifiche
  useEffect(() => {
    if (!form.name.trim()) return;
    if (JSON.stringify(form) === lastSaved.current) return;
    const t = setTimeout(async () => {
      setSaving(true);
      try { await persist(form); }
      catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
      finally { setSaving(false); }
    }, 900);
    return () => clearTimeout(t);
  }, [form, persist]);

  const runPreview = useCallback((f) => {
    setPreviewing(true);
    api.post("/positions/preview", {
      keywords: f.keywords, exclude_keywords: f.exclude_keywords,
      body_keywords: f.body_keywords, body_exclude_keywords: f.body_exclude_keywords, target_dms: f.target_dms,
      industry_ids: f.industry_ids, min_employees: f.min_employees, max_employees: f.max_employees,
      company_country_codes: f.company_country_codes, company_cities: f.company_cities,
      company_zones: f.company_zones, job_country_codes: f.job_country_codes, job_regions: f.job_regions,
      tech_stack: f.tech_stack, contract_type: f.contract_type, company_type: f.company_type, remote: f.remote,
      history_days: f.history_days, import_limit: f.import_limit,
    }).then((r) => setPreview(r.data)).catch(() => {}).finally(() => setPreviewing(false));
  }, []);

  const activate = async () => {
    if (!form.name.trim()) return toast.error("Inserisci un nome posizione");
    const cost = preview
      ? `${preview.estimated_cost_eur}€ (~${preview.estimated_credits} crediti · fino a ${preview.opportunities_to_import} opportunità)`
      : "costo stimato non disponibile";
    const ok = window.confirm(
      `Confermi l'attivazione della posizione?\n\nVerrà eseguito il fetch su TheirStack fino a raggiungere il numero di opportunità impostato. Consuma CREDITI REALI (1 credito per annuncio scaricato; possono servire più annunci per opportunità per via della deduplica).\n\nCosto stimato: ${cost}.`
    );
    if (!ok) return;
    setSaving(true);
    try {
      const curPid = await persist(form);
      const { data } = await api.post(`/positions/${curPid}/activate`);
      const r = data.results || {};
      const nuove = (r.created || 0) + (r.reactivation || 0);
      if (nuove === 0) {
        if (data.rate_limited) {
          toast.error("Limite orario TheirStack raggiunto (50 richieste/ora). Nessuna opportunità importata: riprova tra qualche minuto con «Aggiorna» sulla posizione.");
        } else if (data.error) {
          toast.error("Errore TheirStack: nessuna opportunità importata. Riprova più tardi.");
        } else {
          toast.info("Nessun annuncio reale trovato con questi filtri. Allarga keyword/zona o periodo e riprova.");
        }
        navigate("/posizioni");
        return;
      }
      const extra = [];
      if (r.signal_added) extra.push(`${r.signal_added} segnali su opportunità esistenti`);
      if (r.excluded) extra.push(`${r.excluded} aziende escluse`);
      if (r.cooldown) extra.push(`${r.cooldown} in cooldown`);
      const tail = extra.length ? ` (${extra.join(", ")})` : "";
      toast.success(`${data.jobs_processed} annunci elaborati → ${nuove} nuove opportunità${tail}`);
      navigate("/posizioni");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const deletePosition = async () => {
    if (!pidRef.current) { navigate("/posizioni"); return; }
    if (!window.confirm(`Eliminare definitivamente la posizione "${form.name || "senza nome"}"? L'azione non è reversibile.`)) return;
    try {
      await api.delete(`/positions/${pidRef.current}`);
      toast.success("Posizione eliminata");
      navigate("/posizioni");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="flex flex-col gap-6 max-w-5xl">
      <h1 className="text-2xl font-semibold tracking-tight text-ink">{id ? "Modifica posizione" : "Nuova posizione"}</h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Form */}
        <div className="lg:col-span-2 flex flex-col gap-5 bg-white border border-line rounded-[8px] p-6">
          <div>
            <label className="text-sm font-semibold text-ink block mb-1.5">Nome posizione</label>
            <input data-testid="position-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Es. Sales - Nord Italia" className="w-full bg-white border border-line rounded-[6px] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand transition-colors" />
          </div>

          <TagInput testid="kw-input" label="Keyword annuncio (titolo)" value={form.keywords} onChange={(v) => set("keywords", v)} placeholder="Digita e premi Invio (es. Sales)" />
          <TagInput testid="exclude-kw-input" label="Keyword da escludere (titolo annuncio)" value={form.exclude_keywords} onChange={(v) => set("exclude_keywords", v)} placeholder="Es. stage, intern, junior" />
          <TagInput testid="body-kw-input" label="Keyword nel testo annuncio (corpo/descrizione)" value={form.body_keywords} onChange={(v) => set("body_keywords", v)} placeholder="Parole intere nel corpo (es. fotovoltaico, provvigioni)" />
          <TagInput testid="body-exclude-kw-input" label="Keyword da escludere dal testo annuncio (corpo/descrizione)" value={form.body_exclude_keywords} onChange={(v) => set("body_exclude_keywords", v)} placeholder="Parole intere da escludere nel corpo (es. stage, tirocinio)" />
          <DmTargetInput value={form.target_dms} onChange={(v) => set("target_dms", v)} suggestions={DM_OPTIONS} />
          <div>
            <CountryMultiSelect testid="dm-location" label="Località Decision Maker — Paese (opzionale)" value={form.target_dm_location} onChange={(v) => set("target_dm_location", v)} />
            <p className="text-[11px] text-faint mt-1">Filtra le persone trovate su Prospeo per Paese di residenza. Lascia vuoto per non filtrare per località.</p>
          </div>
          <IndustryPicker testid="industry-picker" industries={industries} value={form.industry_ids} onChange={(v) => set("industry_ids", v)} />
          <EmployeesFilter min={form.min_employees} max={form.max_employees} onChange={(mn, mx) => setForm((f) => ({ ...f, min_employees: mn, max_employees: mx }))} />
          <div>
            <label className="text-sm font-semibold text-ink block mb-1.5">Tipo di azienda (Company Type)</label>
            <select data-testid="company-type-select" value={form.company_type ?? ""} onChange={(e) => set("company_type", e.target.value === "" ? null : e.target.value)} className="w-full bg-white border border-line rounded-[6px] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand transition-colors">
              <option value="">Tutte le aziende</option>
              <option value="direct_employer">Solo datori di lavoro diretti (escludi agenzie di recruiting)</option>
              <option value="recruiting_agency">Solo agenzie di recruiting</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <CountryMultiSelect testid="hq-country" label="Sede azienda — Paese (HQ)" value={form.company_country_codes} onChange={(v) => set("company_country_codes", v)} />
            <TagInput testid="hq-city-input" label="Sede azienda — Città" value={form.company_cities} onChange={(v) => set("company_cities", v)} placeholder="Es. Milano, Roma" />
          </div>
          {form.company_country_codes.length === 0 && (
            <div className="flex items-start gap-2 bg-[#FFF4E5] text-[#B45309] rounded-[6px] p-2.5 text-xs -mt-2">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" strokeWidth={1.5} />
              <span><b>Nessun filtro sulla sede azienda.</b> Verranno incluse anche multinazionali estere che pubblicano annunci in Italia (es. Tory Burch, JYSK). Imposta il Paese HQ = Italia per avere solo aziende con sede in Italia.</span>
            </div>
          )}
          <ChipMulti testid="region-select" label="Zona ricerca annuncio (regioni IT)" options={REGION_OPTIONS} value={form.job_regions} onChange={(v) => set("job_regions", v)} />
          <CountryMultiSelect testid="job-country" label="Paese di pubblicazione annuncio" value={form.job_country_codes} onChange={(v) => set("job_country_codes", v)} />
          <TagInput testid="tech-input" label="Tech stack (opzionale)" value={form.tech_stack} onChange={(v) => set("tech_stack", v)} placeholder="Es. salesforce, hubspot" />

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-semibold text-ink block mb-1.5">Recupero storico</label>
              <select data-testid="history-select" value={form.history_days} onChange={(e) => set("history_days", parseInt(e.target.value))} className="w-full bg-white border border-line rounded-[6px] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand transition-colors">
                {HISTORY_OPTIONS.map((d) => <option key={d} value={d}>{d} giorni</option>)}
              </select>
            </div>
            <div>
              <label className="text-sm font-semibold text-ink block mb-1.5">Modalità</label>
              <select data-testid="remote-select" value={form.remote === null ? "" : String(form.remote)} onChange={(e) => set("remote", e.target.value === "" ? null : e.target.value === "true")} className="w-full bg-white border border-line rounded-[6px] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand transition-colors">
                <option value="">Tutte</option>
                <option value="true">Remote</option>
                <option value="false">On-site</option>
              </select>
            </div>
          </div>

          <div>
            <label className="text-sm font-semibold text-ink block mb-1.5">Opportunità (aziende) da importare</label>
            <div className="flex items-center gap-2">
              <input
                data-testid="import-limit-input"
                type="number"
                min={1}
                value={form.import_limit ?? ""}
                onChange={(e) => set("import_limit", e.target.value === "" ? null : Math.max(1, parseInt(e.target.value)))}
                placeholder="Predefinito globale (50)"
                className="w-48 bg-white border border-line rounded-[6px] px-3 py-2 text-sm placeholder-faint focus:outline-none focus:ring-2 focus:ring-brand transition-colors"
              />
              {preview && !preview.used_mock && (
                <button
                  type="button"
                  data-testid="import-all-button"
                  onClick={() => set("import_limit", preview.estimated_opportunities)}
                  className="flex items-center gap-1.5 bg-brand-soft text-brand-dark font-medium rounded-[6px] px-3 py-2 text-sm hover:bg-brand hover:text-ink transition-colors whitespace-nowrap"
                >
                  <Check className="w-3.5 h-3.5" strokeWidth={2} /> Importa tutte ({preview.estimated_opportunities})
                </button>
              )}
              {form.import_limit != null && (
                <button
                  type="button"
                  data-testid="import-limit-reset"
                  onClick={() => set("import_limit", null)}
                  className="text-xs text-subtle hover:text-destructive transition-colors"
                >
                  Reimposta
                </button>
              )}
            </div>
            <p className="text-[11px] text-faint mt-1">Numero di <b>opportunità (aziende) nuove</b> da importare. Il sistema scarica annunci finché non raggiunge questo numero di aziende (deduplica gestita), con un tetto di sicurezza pari a 5× annunci. TheirStack addebita 1 credito per annuncio scaricato, quindi il costo può essere leggermente superiore al numero di opportunità. Lascia vuoto per usare il limite globale.</p>
          </div>
        </div>

        {/* Live preview */}
        <div className="flex flex-col gap-4">
          <div className="bg-white border border-line rounded-[8px] p-5 lg:sticky lg:top-20">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="w-4 h-4 text-brand-dark" strokeWidth={1.5} />
              <h2 className="text-sm font-semibold text-ink">Anteprima stima</h2>
            </div>
            <button
              data-testid="generate-preview-button"
              onClick={() => runPreview(form)}
              disabled={previewing}
              className="w-full flex items-center justify-center gap-1.5 bg-brand text-ink font-medium rounded-[6px] px-4 py-2 text-sm hover:bg-brand-hover disabled:opacity-50 transition-colors mb-4"
            >
              <TrendingUp className="w-4 h-4" strokeWidth={1.5} />
              {previewing ? "Calcolo in corso…" : preview ? "Aggiorna anteprima" : "Genera anteprima"}
            </button>
            {!preview ? (
              <p className="text-xs text-faint">Imposta i filtri e clicca <b>Genera anteprima</b>. La stima è gratuita ma il piano TheirStack consente max 50 richieste/ora, quindi generala quando hai finito di configurare i filtri.</p>
            ) : (
              <div className="flex flex-col gap-4">
                {preview.used_mock && (
                  <div data-testid="preview-unavailable" className="flex items-start gap-2 bg-[#FFE5E5] text-destructive rounded-[6px] p-2.5 text-xs">
                    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" strokeWidth={1.5} />
                    <span>
                      {preview.rate_limited
                        ? "Limite orario TheirStack raggiunto (50 richieste/ora). I numeri reali non sono disponibili ora: riprova tra qualche minuto."
                        : "Stima non disponibile (API TheirStack non raggiungibile). Riprova più tardi."}
                    </span>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <Stat testid="stat-ads" label="Annunci totali" value={preview.used_mock ? "—" : preview.total_ads} />
                  <Stat testid="stat-companies" label="Aziende uniche" value={preview.used_mock ? "—" : preview.unique_companies} />
                  <Stat testid="stat-opps" label="Opp. stimate" value={preview.used_mock ? "—" : preview.estimated_opportunities} highlight />
                  <Stat testid="stat-credits" label="Costo importazione" value={preview.used_mock ? "—" : `${preview.estimated_cost_eur}€`} sub={preview.used_mock ? "" : `${preview.estimated_credits} crediti`} highlight />
                </div>

                {!preview.used_mock && (
                <div className="rounded-[6px] border border-line p-3 text-xs flex flex-col gap-1.5">
                  <div className="flex justify-between"><span className="text-subtle">Opportunità importate</span><span className="font-medium text-ink">{preview.opportunities_to_import} / {preview.estimated_opportunities}</span></div>
                  <div className="flex justify-between"><span className="text-subtle">Annunci scaricati (stima)</span><span className="font-medium text-ink">~{preview.estimated_credits} / {preview.total_ads}</span></div>
                  <div className="flex justify-between"><span className="text-subtle">Costo di questa importazione</span><span className="font-semibold text-brand-dark">{preview.estimated_cost_eur}€</span></div>
                  <div className="flex justify-between"><span className="text-subtle">Costo se importi TUTTI ({preview.total_ads})</span><span className="font-medium text-ink">{preview.full_cost_eur}€</span></div>
                  <p className="text-[10px] text-faint mt-1">TheirStack addebita 1 credito (~{preview.credit_eur}€) per annuncio importato. La preview è gratuita e non consuma crediti.</p>
                </div>
                )}

                {!preview.used_mock && (
                <div>
                  <p className="text-[11px] text-faint uppercase tracking-wide mb-2">Freschezza annunci</p>
                  <div className="flex flex-col gap-1.5 text-xs">
                    <FreshBar label="Ultimi 7 giorni" v={preview.temporal_breakdown.last_7} total={preview.total_ads} />
                    <FreshBar label="8–30 giorni" v={preview.temporal_breakdown.d8_30} total={preview.total_ads} />
                    <FreshBar label="30–90 giorni" v={preview.temporal_breakdown.d30_90} total={preview.total_ads} />
                    {preview.temporal_breakdown.over_90 > 0 && <FreshBar label="Oltre 90 giorni" v={preview.temporal_breakdown.over_90} total={preview.total_ads} />}
                  </div>
                </div>
                )}

                {!preview.used_mock && preview.warning && (
                  <div className="flex items-start gap-2 bg-[#FFF4E5] text-[#B45309] rounded-[6px] p-2.5 text-xs">
                    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" strokeWidth={1.5} />
                    <span>{preview.warning}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Barra azioni sempre visibile */}
      <div className="sticky bottom-4 z-20 bg-white border border-line rounded-[8px] p-3 shadow-lg flex items-center justify-between gap-3">
        <div data-testid="autosave-indicator" className="text-xs pl-1">
          {saving ? (
            <span className="text-subtle">Salvataggio automatico…</span>
          ) : savedAt ? (
            <span className="inline-flex items-center gap-1 text-brand-dark"><Check className="w-3.5 h-3.5" strokeWidth={2} /> Modifiche salvate</span>
          ) : (
            <span className="text-faint">Le modifiche si salvano automaticamente</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="delete-position-button" onClick={deletePosition} className="flex items-center justify-center gap-1.5 bg-white border border-line text-subtle font-medium rounded-[6px] px-4 py-2.5 text-xs hover:text-destructive hover:border-destructive transition-colors">
            <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} /> Elimina
          </button>
          <button data-testid="activate-position-button" onClick={activate} disabled={saving} className="flex items-center justify-center gap-1.5 bg-brand text-ink font-semibold rounded-[6px] px-5 py-2.5 text-sm hover:bg-brand-hover disabled:opacity-50 transition-colors">
            <Play className="w-4 h-4" strokeWidth={1.5} /> {saving ? "Attendere…" : (form.status === "attiva" ? "Ri-attiva / importa" : "Attiva posizione")}
          </button>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, sub, highlight, testid }) {
  return (
    <div data-testid={testid} className={`rounded-[6px] p-3 ${highlight ? "bg-brand-soft" : "bg-background"}`}>
      <p className="text-[11px] text-subtle">{label}</p>
      <p className={`text-lg font-bold ${highlight ? "text-brand-dark" : "text-ink"}`}>{value}</p>
      {sub && <p className="text-[10px] text-faint">{sub}</p>}
    </div>
  );
}

function FreshBar({ label, v, total }) {
  const pct = total > 0 ? Math.round((v / total) * 100) : 0;
  return (
    <div>
      <div className="flex justify-between text-[11px] text-subtle mb-0.5">
        <span>{label}</span><span>{v}</span>
      </div>
      <div className="h-1.5 bg-background rounded-full overflow-hidden">
        <div className="h-full bg-brand rounded-full" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}
